import { useMutation } from '@tanstack/react-query';

import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { useObjectPickerStore } from '@stores/useObjectPickerStore';
import { detectCollection } from '@utils/api';
import { AREA_MODE } from '@utils/constants';
import { DETECTION_OUTCOME, classifyDetectionResult } from '@utils/detectionOutcome';

import useNotify from '@hooks/useNotify';
import { useShowCollectionResults } from '@hooks/useShowCollectionResults';

/**
 * Runs detection for the collection configured in the Object Picker wizard.
 *
 * Wraps `POST /collections/{uuid}/detect` in a React Query mutation. Sends the
 * project and the area of interest — the drawn polygon (EPSG:3857 ring), or
 * `'all'` for the entire project.
 *
 * The run is two-phase, and the phases are split across wizard steps so the user
 * is only ever asked once:
 *   1. `previewDetection()` runs on the Area step's Continue (no `confirm`, so
 *      nothing starts). The server's verdict decides what happens next.
 *   2. `confirmRun()` runs from the Run step — reached only when the server said
 *      a run is needed — and repeats the request with `confirm: true`.
 *
 * An area that's already been detected never reaches the Run step: there's
 * nothing to confirm, so the collection is selected on the map and the wizard
 * closes with its existing results on screen.
 */
export const useRunDetection = () => {
	const { project } = useProject();
	const { addToast } = useToast();
	const { checkForNotifications } = useNotify();
	const showCollectionResults = useShowCollectionResults();

	const sourceCollectionId = useObjectPickerStore(s => s.sourceCollectionId);
	const areaMode = useObjectPickerStore(s => s.areaMode);
	const aoiPolygon = useObjectPickerStore(s => s.aoiPolygon);
	const closeAndReset = useObjectPickerStore(s => s.closeAndReset);
	const nextStep = useObjectPickerStore(s => s.nextStep);
	const setActiveJobUuid = useObjectPickerStore(s => s.setActiveJobUuid);
	const preview = useObjectPickerStore(s => s.detectionPreview);
	const setDetectionPreview = useObjectPickerStore(s => s.setDetectionPreview);

	const { mutate, isPending, variables } = useMutation({
		mutationFn: ({ collectionUuid, projectUuid, aoi, confirm }) =>
			detectCollection(collectionUuid, { projectUuid, aoi, confirm }),
		onSuccess: (result, { collectionUuid }) => {
			const outcome = classifyDetectionResult(result);

			// A run is needed and hasn't started. Carry the server's verdict into
			// the Run step, which exists purely to collect the one confirmation.
			if (outcome === DETECTION_OUTCOME.NEEDS_CONFIRMATION) {
				// The wizard may have been closed while this preview was in flight.
				// `closeAndReset` puts the wizard back on step 0, so advancing now
				// would strand a reopened picker on the Area step with no selection.
				// Read through `getState()` so the check reflects when the response
				// landed, not the render that fired the request. Only this branch is
				// guarded: a STARTED job is real and must still be reported below.
				if (!useObjectPickerStore.getState().isOpen) return;
				setDetectionPreview(result);
				nextStep();
				return;
			}

			if (outcome === DETECTION_OUTCOME.STARTED) {
				addToast({
					id: 'detection-success',
					bg: 'success',
					title: 'Running detection...',
					message:
						'Detection is running. Depending on the area selected, this may take several minutes. You will be notified when it is complete.',
					autohide: true,
				});
				setActiveJobUuid(result?.job_uuid ?? null);
				closeAndReset();
				return;
			}

			// `null` means the response matched no known outcome. Report it and
			// leave the wizard open to retry, exactly as `onError` does — loading
			// "existing results" here would claim success for an unknown fate.
			if (outcome === null) {
				addToast({
					id: 'detection-error',
					bg: 'danger',
					title: 'Error running detection',
					message:
						'The detection service returned an unexpected response. Please try again.',
					autohide: false,
				});
				return;
			}

			// Already detected — nothing to run, and nothing to confirm. Show what
			// exists instead of walking the user through a run that can't happen.
			// Without this the wizard would close with nothing visibly changed: the
			// collection isn't necessarily the one the map is showing.
			showCollectionResults(collectionUuid);
			addToast({
				id: 'detection-success',
				bg: 'success',
				title: 'Detection results available',
				message: 'This area has already been analyzed. Loading existing results.',
				autohide: true,
			});
			closeAndReset();
		},
		onError: error => {
			addToast({
				id: 'detection-error',
				bg: 'danger',
				title: 'Error running detection',
				message: error?.message ?? 'Unknown error running detection.',
				autohide: false,
			});
		},
		// Refresh notifications whether the run started or failed.
		onSettled: () => checkForNotifications(),
	});

	/**
	 * Validate the wizard's configuration and resolve the request payload.
	 * Returns `null` (after surfacing a toast) when something is missing.
	 */
	const resolveRequest = () => {
		const projectUuid = project?.uuid;
		// Draw mode sends the polygon ring; entire-project sends "all".
		const aoi = areaMode === AREA_MODE.DRAW ? aoiPolygon : 'all';

		const missingArea = areaMode === AREA_MODE.DRAW && !aoiPolygon;
		if (!sourceCollectionId || !projectUuid || missingArea) {
			addToast({
				id: 'detection-error',
				bg: 'danger',
				title: 'Could not start detection',
				message: missingArea
					? 'Draw an area of interest first, or choose the entire project.'
					: 'Missing collection or project. Please try again.',
				autohide: false,
			});
			return null;
		}

		return { collectionUuid: sourceCollectionId, projectUuid, aoi };
	};

	/**
	 * Phase 1 — ask the server what a run would do. Starts nothing, and gates
	 * entry to the Run step so it's only ever shown when there's something to
	 * confirm.
	 */
	const previewDetection = () => {
		const request = resolveRequest();
		if (!request) return;
		mutate({ ...request, confirm: false });
	};

	/** Phase 2 — the user confirmed on the Run step. Start the run. */
	const confirmRun = () => {
		const request = resolveRequest();
		if (!request) return;
		mutate({ ...request, confirm: true });
	};

	return {
		previewDetection,
		confirmRun,
		// Each step's button tracks only its own request. Sharing one flag would
		// leave the Run step's confirm button showing a spinner for the preview
		// that just landed the user there — briefly unclickable for a run nobody
		// has asked for yet. `variables` is only meaningful while pending.
		isPreviewing: isPending && variables?.confirm === false,
		isConfirming: isPending && variables?.confirm === true,
		isRunning: isPending,
		preview,
		outcome: preview ? classifyDetectionResult(preview) : null,
	};
};
